import React from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Tooltip } from "@mui/material";
import { ParentGroupsDropDownAlertsPage } from "./ParentGroupsDropDownAlertsPage";
import { SiteDropDownAlertsPage } from "./SiteDropDownAlertsPage";
import { CameraDropDownAlertsPage } from "./CameraDropDownAlertsPage";
import { StartDateDropDownAlerts } from "./StartDateDropDownAlerts";
import { EndDateDropDownAlerts } from "./EndDateDropDownAlerts";
import { StartTimeDropDownAlerts } from "./StartTimeDropDownAlerts";
import { EndTimeDropDownAlerts } from "./EndTimeDropDownAlerts";
import { updateSearchParamValues } from "../../utils/updateSearchParamValues";
import { getAlertsWithQuerry } from "./AlertsUtils/getAlertsWithQuerry";
import { yyyymmddGenerator } from "../Analytics/AnalyticsUtils/yyyymmddGenerator";
import { checkIfCurrentDate } from "./AlertsUtils/checkIfCurrentDate";
import { useAlertsStore } from "./AlertsStore/alertsStore";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { AlertsScopeLine } from "./AlertsScopeLine";
import {
  formatOtherAlertsLabel,
  formatSiteCount,
} from "./AlertsUtils/alertScope";
import { useAlertScopeView } from "./AlertsUtils/useAlertScopeView";
import {
  alertsApplyFiltersButtonClassName,
  alertsFilterDateFieldClassName,
  alertsFilterDateFieldStackedClassName,
  alertsFilterFieldClassName,
  alertsFilterFieldStackedClassName,
  alertsFilterFieldsRowClassName,
  alertsFilterFieldsStackedClassName,
  shouldStackAlertsFilters,
} from "./AlertsUtils/alertsFilterBar";

export const FiltersAlerts = ({
  navBarCollapsed,
  userFilters,
  setUserFilters,
  navigate,
  setSiteOptions,
  setSiteDropDownValue,
  setCameraOptions,
  setCameraDropDownValue,
  setLoadingSites,
  setRefreshInterval,
  parentAccounts,
  parentIds,
  parentAccountsLoading,
  siteOptions,
  siteDropDownValue,
  loadingSites,
  setLoadingCameras,
  cameraOptions,
  cameraDropDownValue,
  loadingCameras,
  setFetchingFilteredData,
  setAlertData,
  setSelectedRow,
  setSeeMoreURL,
  setMP4URL,
  setSelectedButtonsFilled,
  setThumbnailVideos,
  setDateOutsideRange,
  setPage,
  setVideoClipLoading,
  setAlertsToDisplay,
  setPageLoading,
  setTimeFiltersDisabled,
  warningIssued,
  setWarningIssued,
  timeFiltersDisabled,
  setFiltersApplied,
  pageLoadedNumber,
  setPageLoadedNumber,
  searchParams,
  setSearchParams,
  detectionType,
  setDetectionType,
  setSelectedAlertCameraInfo,
  setIgnoreZonesSelectedAlert,
  setAlertsIndexOrder,
  setCurrentName,
  setRefreshingData,
  setRefreshingToken,
  refreshingToken,
  setAlertLabels,
  setPeriodHours,
  setQueryLimitHit,
  refreshInterval,
  setOutcomeFilterType,
  setForwardArrowDisabled,
  setBackArrowDisabled,
  setShowRefreshButton,
  setRowsSortable,
  setAlertsRefreshing,
  getCurrentSelectedRowKey,
  setVideoProcessing,
}: {
  navBarCollapsed: any;
  userFilters: any;
  setUserFilters: any;
  navigate: any;
  setSiteOptions: any;
  setSiteDropDownValue: any;
  setCameraOptions: any;
  setCameraDropDownValue: any;
  setLoadingSites: any;
  setRefreshInterval: any;
  parentAccounts: any;
  parentIds: any;
  parentAccountsLoading: any;
  siteOptions: any;
  siteDropDownValue: any;
  loadingSites: any;
  setLoadingCameras: any;
  cameraOptions: any;
  cameraDropDownValue: any;
  loadingCameras: any;
  setFetchingFilteredData: any;
  setAlertData: any;
  setSelectedRow: any;
  setSeeMoreURL: any;
  setMP4URL: any;
  setSelectedButtonsFilled: any;
  setThumbnailVideos: any;
  setDateOutsideRange: any;
  setPage: any;
  setVideoClipLoading: any;
  setAlertsToDisplay: any;
  setPageLoading: any;
  setTimeFiltersDisabled: any;
  warningIssued: any;
  setWarningIssued: any;
  timeFiltersDisabled: any;
  setFiltersApplied: any;
  pageLoadedNumber: any;
  setPageLoadedNumber: any;
  searchParams: any;
  setSearchParams: any;
  detectionType: any;
  setDetectionType: any;
  setSelectedAlertCameraInfo: any;
  setIgnoreZonesSelectedAlert: any;
  setAlertsIndexOrder: any;
  setCurrentName: any;
  setRefreshingData: any;
  setRefreshingToken: any;
  refreshingToken: any;
  setAlertLabels: any;
  setPeriodHours: any;
  setQueryLimitHit: any;
  refreshInterval: any;
  setOutcomeFilterType: any;
  setForwardArrowDisabled: any;
  setBackArrowDisabled: any;
  setShowRefreshButton: any;
  setRowsSortable: any;
  setAlertsRefreshing: any;
  getCurrentSelectedRowKey: () => string | null;
  setVideoProcessing: any;
}) => {
  const { mutedAlertSites, mutedAlertCameras } = useAlertsStore();
  const { flags } = useFeatureFlag();
  const showAlertsScopeContext = Boolean(flags.showAlertsScopeContext);
  const appliedGroupId =
    typeof searchParams?.get === "function" ? searchParams.get("group") : null;
  const { scope, other } = useAlertScopeView({
    enabled: showAlertsScopeContext,
    parentAccounts: Array.isArray(parentAccounts) ? parentAccounts : [],
    appliedGroupId,
  });
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [filtersCollapsed, setFiltersCollapsed] = React.useState(false);
  const stackFilters = shouldStackAlertsFilters(containerWidth);
  const fieldClassName = stackFilters
    ? alertsFilterFieldStackedClassName
    : alertsFilterFieldClassName;
  const dateFieldClassName = stackFilters
    ? alertsFilterDateFieldStackedClassName
    : alertsFilterDateFieldClassName;

  React.useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }
    const updateWidth = () => {
      setContainerWidth(node.clientWidth);
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const applyFilters = (filtersOverride?: any) => {
    const filters = filtersOverride ?? userFilters;
    let searchingPastDates = false;
    if (filters.end_date) {
      searchingPastDates = checkIfCurrentDate(
        yyyymmddGenerator(filters.end_date)
      );
    }

    if (filters.start_date) {
      if (
        yyyymmddGenerator(filters.start_date) !==
        yyyymmddGenerator(filters?.end_date)
      ) {
        searchingPastDates = true;
      }
    }

    if (filters.start_time || filters.end_time) {
      searchingPastDates = true;
    }

    if (searchingPastDates) {
      setAlertsRefreshing(false);
    }
    localStorage.setItem("pageLoadedNumber", JSON.stringify(0));
    localStorage.setItem("latestAlertFilters", JSON.stringify(filters));
    setPage(0);
    let newNumber = Number(pageLoadedNumber);
    newNumber += 1;
    setPageLoadedNumber(newNumber);
    localStorage.setItem("pageLoadedNumber", `${newNumber}`);
    setRefreshingData(false);
    setFiltersApplied(true);
    setFetchingFilteredData(true);
    setDateOutsideRange(false);
    localStorage.setItem("newQueryRunning", "true");
    updateSearchParamValues(filters, searchParams, setSearchParams, "All");
    getAlertsWithQuerry(
      setAlertData,
      navigate,
      setSelectedRow,
      setFetchingFilteredData,
      setSeeMoreURL,
      setMP4URL,
      filters.parent_group,
      filters.site,
      filters.camera,
      yyyymmddGenerator(filters.start_date),
      yyyymmddGenerator(filters.end_date),
      filters.start_time,
      filters.end_time,
      filters.filter_type,
      setSelectedButtonsFilled,
      setThumbnailVideos,
      setDateOutsideRange,
      setPage,
      setVideoClipLoading,
      setAlertsToDisplay,
      setPageLoading,
      setSelectedAlertCameraInfo,
      setIgnoreZonesSelectedAlert,
      setAlertsIndexOrder,
      setRefreshInterval,
      setCurrentName,
      setUserFilters,
      setRefreshingData,
      newNumber,
      refreshInterval,
      filters,
      cameraOptions,
      siteOptions,
      setRefreshingToken,
      refreshingToken,
      setAlertLabels,
      setPeriodHours,
      setQueryLimitHit,
      setForwardArrowDisabled,
      setBackArrowDisabled,
      setShowRefreshButton,
      setRowsSortable,
      setAlertsRefreshing,
      getCurrentSelectedRowKey,
      mutedAlertSites,
      mutedAlertCameras,
      setVideoProcessing
    );
    setDetectionType("All");
    setOutcomeFilterType("All");
  };

  const handleViewAll = () => {
    const nextFilters = {
      ...userFilters,
      parent_group: "",
      site: "",
      camera: "",
    };
    setUserFilters(nextFilters);
    setSiteDropDownValue([]);
    setCameraDropDownValue([]);
    setSiteOptions([]);
    setCameraOptions([]);
    applyFilters(nextFilters);
  };

  const applyFiltersButton = (
    <button
      className={alertsApplyFiltersButtonClassName}
      onClick={() => applyFilters()}
      disabled={false}
    >
      Apply Filters
    </button>
  );

  const filtersToggleButton = (
    <Tooltip
      title={filtersCollapsed ? "Expand filters" : "Collapse filters"}
      placement="top"
    >
      <button
        type="button"
        aria-label={filtersCollapsed ? "Expand filters" : "Collapse filters"}
        aria-expanded={!filtersCollapsed}
        aria-controls="alerts-filters-fields"
        onClick={() => setFiltersCollapsed((current) => !current)}
        className="inline-flex items-center justify-center text-actuate-blue"
      >
        {filtersCollapsed ? (
          <ExpandMoreIcon sx={{ fontSize: 22 }} />
        ) : (
          <ExpandLessIcon sx={{ fontSize: 22 }} />
        )}
      </button>
    </Tooltip>
  );

  const filterFields = (
    <>
      <div className={fieldClassName}>
        <ParentGroupsDropDownAlertsPage
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          navigate={navigate}
          setSiteOptions={setSiteOptions}
          setSiteDropDownValue={setSiteDropDownValue}
          setCameraOptions={setCameraOptions}
          setCameraDropDownValue={setCameraDropDownValue}
          setLoadingSites={setLoadingSites}
          setRefreshInterval={setRefreshInterval}
          parentAccounts={parentAccounts}
          parentIds={parentIds}
          parentAccountsLoading={parentAccountsLoading}
          navBarCollapsed={navBarCollapsed}
        />
      </div>
      <div className={fieldClassName}>
        <SiteDropDownAlertsPage
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          siteOptions={siteOptions}
          setCameraOptions={setCameraOptions}
          setCameraDropDownValue={setCameraDropDownValue}
          navigate={navigate}
          siteDropDownValue={siteDropDownValue}
          setSiteDropDownValue={setSiteDropDownValue}
          loadingSites={loadingSites}
          setLoadingCameras={setLoadingCameras}
          navBarCollapsed={navBarCollapsed}
        />
      </div>
      <div className={fieldClassName}>
        <CameraDropDownAlertsPage
          setUserFilters={setUserFilters}
          cameraOptions={cameraOptions}
          cameraDropDownValue={cameraDropDownValue}
          setCameraDropDownValue={setCameraDropDownValue}
          siteOptions={siteOptions}
          loadingCameras={loadingCameras}
          navBarCollapsed={navBarCollapsed}
        />
      </div>
      <div className={dateFieldClassName}>
        <StartDateDropDownAlerts
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          siteOptions={siteOptions}
          setTimeFiltersDisabled={setTimeFiltersDisabled}
          warningIssued={warningIssued}
          setWarningIssued={setWarningIssued}
        />
      </div>
      <div className={dateFieldClassName}>
        <EndDateDropDownAlerts
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          siteOptions={siteOptions}
          setTimeFiltersDisabled={setTimeFiltersDisabled}
          warningIssued={warningIssued}
          setWarningIssued={setWarningIssued}
        />
      </div>
      <div className={dateFieldClassName}>
        <StartTimeDropDownAlerts
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          navigate={navigate}
          setFetchingFilteredData={setFetchingFilteredData}
          setAlertData={setAlertData}
          setSelectedRow={setSelectedRow}
          setSeeMoreURL={setSeeMoreURL}
          setMP4URL={setMP4URL}
          siteOptions={siteOptions}
          setSelectedButtonsFilled={setSelectedButtonsFilled}
          setThumbnailVideos={setThumbnailVideos}
          setDateOutsideRange={setDateOutsideRange}
          setPage={setPage}
          setVideoClipLoading={setVideoClipLoading}
          setAlertsToDisplay={setAlertsToDisplay}
          setPageLoading={setPageLoading}
          timeFiltersDisabled={timeFiltersDisabled}
        />
      </div>
      <div className={dateFieldClassName}>
        <EndTimeDropDownAlerts
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          navigate={navigate}
          setFetchingFilteredData={setFetchingFilteredData}
          setAlertData={setAlertData}
          setSelectedRow={setSelectedRow}
          setSeeMoreURL={setSeeMoreURL}
          setMP4URL={setMP4URL}
          siteOptions={siteOptions}
          setSelectedButtonsFilled={setSelectedButtonsFilled}
          setThumbnailVideos={setThumbnailVideos}
          setDateOutsideRange={setDateOutsideRange}
          setPage={setPage}
          setVideoClipLoading={setVideoClipLoading}
          setAlertsToDisplay={setAlertsToDisplay}
          setPageLoading={setPageLoading}
          timeFiltersDisabled={timeFiltersDisabled}
        />
      </div>
    </>
  );

  // The collapse toggle sits to the right of Apply Filters at every width
  // so operators can reclaim the strip for the alert list on wide screens
  // too; the collapsed state survives window resizes.
  const filterActions = (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      {applyFiltersButton}
      {filtersToggleButton}
    </div>
  );

  const scopeLine =
    showAlertsScopeContext && scope?.narrower && scope.selected ? (
      <AlertsScopeLine
        pathLabel={scope.selected.path}
        siteLabel={formatSiteCount(scope.selected.siteCount)}
        otherLabel={formatOtherAlertsLabel(other)}
        onViewAll={handleViewAll}
      />
    ) : null;

  return (
    <>
      <div
        ref={containerRef}
        className={
          filtersCollapsed
            ? "flex w-full justify-end px-4 py-1"
            : "flex w-full items-end gap-3 px-4 pb-4 pt-4"
        }
      >
        {filtersCollapsed ? (
          filtersToggleButton
        ) : (
          <div className="flex w-full items-start gap-3">
            <div
              id="alerts-filters-fields"
              className={
                stackFilters
                  ? `${alertsFilterFieldsStackedClassName} min-w-0 flex-1`
                  : alertsFilterFieldsRowClassName
              }
            >
              {filterFields}
            </div>
            {filterActions}
          </div>
        )}
      </div>
      {scopeLine}
    </>
  );
};
